import { createClient, isMfaChallenge } from '@orbitneststudio/node'
import type { AuthSession as SdkAuthSession, AuthUser as SdkAuthUser } from '@orbitneststudio/node'
import { encryptSessionObjectSync, decryptSessionStringSync } from '@/lib/crypto/session-crypt'

const ANON_KEY = process.env.NEXT_PUBLIC_ORBITNEST_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsInByb2plY3Rfc2x1ZyI6Im94aWdlbmFwcCIsInBlcm1pc3Npb25zIjp7InJlYWQiOnRydWUsImluc2VydCI6dHJ1ZSwidXBkYXRlIjpmYWxzZSwiZGVsZXRlIjpmYWxzZX0sImlhdCI6MTc3ODc3MTY0Nn0.5zki0Xn6EQvz3OicyfrXugV32V1FKYvDEdpKWECLSSw'

const authSdkClient = createClient({ apiKey: ANON_KEY })

export interface AuthCredentials {
  email: string
  password: string
}

export interface AppUser {
  id: string
  email: string
  mail?: string       // backward-compat alias for email
  admin?: boolean     // backward-compat alias for is_admin
  firstname: string
  lastname: string
  login: string
  avatar_url?: string
  tenant_id?: string
  is_admin?: boolean
  metadata?: Record<string, unknown>
}

export interface AuthSession {
  user: AppUser
  token: string
  refreshToken?: string
  expiresAt: number
}

const SESSION_KEY = 'orbitflow_auth_session'
const TOKEN_EXPIRY_MS = 24 * 60 * 60 * 1000

function sdkUserToAppUser(u: SdkAuthUser): AppUser {
  const m = (u.user_metadata || {}) as Record<string, unknown>
  const isAdmin = (m.is_admin || false) as boolean
  return {
    id: u.id,
    email: u.email,
    mail: u.email,
    admin: isAdmin,
    firstname: (m.firstname || m.first_name || u.email.split('@')[0]) as string,
    lastname: (m.lastname || m.last_name || '') as string,
    login: (m.login || u.email) as string,
    avatar_url: m.avatar_url as string | undefined,
    tenant_id: m.tenant_id as string | undefined,
    is_admin: isAdmin,
    metadata: m,
  }
}

function sdkSessionToAppSession(sdk: SdkAuthSession): AuthSession {
  return {
    user: sdkUserToAppUser(sdk.user),
    token: sdk.access_token,
    refreshToken: sdk.refresh_token,
    expiresAt: Date.now() + sdk.expires_in * 1000,
  }
}

export class AuthService {
  async login(credentials: AuthCredentials): Promise<AuthSession> {
    const result = await authSdkClient.auth.signIn({
      email: credentials.email,
      password: credentials.password,
    })
    if (result.error) throw new Error(result.error.message)
    if (isMfaChallenge(result.data)) throw new Error('Multi-factor authentication is required for this account.')
    const session = sdkSessionToAppSession(result.data)
    this.storeSession(session)
    return session
  }

  async register(
    email: string,
    password: string,
    profile: { firstname: string; lastname: string; login?: string; tenant_id?: string },
  ): Promise<{ pending: true; email: string }> {
    const result = await authSdkClient.auth.signUp({
      email,
      password,
      metadata: {
        firstname: profile.firstname,
        lastname: profile.lastname,
        login: profile.login || email,
        tenant_id: profile.tenant_id,
      },
    })
    if (result.error) throw new Error(result.error.message)
    return { pending: true, email: result.data.email }
  }

  async verifySignUp(email: string, code: string): Promise<AuthSession> {
    const result = await authSdkClient.auth.verifySignUp({ email, code })
    if (result.error) throw new Error(result.error.message)
    const session = sdkSessionToAppSession(result.data)
    this.storeSession(session)
    return session
  }

  getSession(): AuthSession | null {
    if (typeof window === 'undefined') return null
    try {
      const raw = localStorage.getItem(SESSION_KEY)
      if (!raw) return null

      let session: AuthSession | null = null
      const decrypted = decryptSessionStringSync(raw)
      if (decrypted) {
        session = decrypted as AuthSession
      } else {
        try { session = JSON.parse(raw) } catch { session = null }
      }

      if (!session?.expiresAt) { this.logout(); return null }
      if (Date.now() >= session.expiresAt - 5 * 60 * 1000) { this.logout(); return null }
      return session
    } catch {
      this.logout()
      return null
    }
  }

  isAuthenticated(): boolean {
    return this.getSession() !== null
  }

  logout(): void {
    if (typeof window === 'undefined') return
    // Do NOT call getSession() here — it would recurse back into logout() if the
    // session is malformed, causing a stack overflow.
    const hasSession = !!localStorage.getItem(SESSION_KEY)
    if (hasSession) {
      authSdkClient.auth.signOut().catch(() => {})
    }
    localStorage.removeItem(SESSION_KEY)
    document.cookie = 'orbitnest-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
  }

  async refreshSession(): Promise<AuthSession | null> {
    const session = this.getSession()
    if (!session?.refreshToken) return null
    try {
      authSdkClient.auth.setSession({
        access_token: session.token,
        refresh_token: session.refreshToken,
        expires_in: Math.max(0, Math.floor((session.expiresAt - Date.now()) / 1000)),
        token_type: 'Bearer',
        user: {
          id: session.user.id,
          email: session.user.email,
          created_at: '',
          updated_at: '',
          user_metadata: session.user.metadata,
        },
      })
      const result = await authSdkClient.auth.refreshSession()
      if (result.error || !result.data) return null
      const updated = sdkSessionToAppSession(result.data)
      this.storeSession(updated)
      return updated
    } catch {
      return null
    }
  }

  getAuthToken(): string | null {
    return this.getSession()?.token ?? null
  }

  initFromRawToken(
    accessToken: string,
    refreshToken: string,
    expiresIn: number,
    rawUser: { id: string; email: string; user_metadata?: Record<string, any> },
  ): AuthSession {
    const m = rawUser.user_metadata || {}
    const user: AppUser = {
      id: rawUser.id,
      email: rawUser.email,
      mail: rawUser.email,
      admin: false,
      firstname: (m.firstname || rawUser.email.split('@')[0]) as string,
      lastname: (m.lastname || '') as string,
      login: (m.login || rawUser.email) as string,
      is_admin: false,
      metadata: m,
    }
    const session: AuthSession = {
      user,
      token: accessToken,
      refreshToken: refreshToken || undefined,
      expiresAt: Date.now() + expiresIn * 1000,
    }
    this.storeSession(session)
    return session
  }

  async testConnection(): Promise<{ success: boolean; error?: string; details?: Record<string, unknown> }> {
    try {
      const result = await authSdkClient.auth.getProfile()
      if (result.error) {
        return { success: false, error: result.error.message, details: { status: result.error.status } }
      }
      return { success: true, details: { server: 'OrbitNest', timestamp: new Date().toISOString() } }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Connection failed' }
    }
  }

  private storeSession(session: AuthSession): void {
    if (typeof window === 'undefined') return
    try {
      const encrypted = encryptSessionObjectSync(session)
      localStorage.setItem(SESSION_KEY, encrypted || JSON.stringify(session))
    } catch {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session))
    }
    const expiry = new Date(session.expiresAt)
    document.cookie = `orbitnest-token=${session.token}; path=/; expires=${expiry.toUTCString()}; SameSite=Lax`
  }
}

export const authService = new AuthService()
